import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { RootState } from "../../redux/store";
import { QueryStatus, axiosInstanceWithPhoneToken } from "../../utils";
import {
  GetDriverKnownPayloadType,
  GetIsDriverKnownResponseType,
} from "./driverKnownApi";
import { GetVehicleLastSessionResponse } from "@qivia/schemas-cards-drivers";

export interface driverKnownState {
  getIsDriverKnownStatus: QueryStatus;
  driver: string | null;
  checkVehicleSessionStatus: QueryStatus;
  lastSession: GetVehicleLastSessionResponse;
}

const initialState: driverKnownState = {
  getIsDriverKnownStatus: "idle",
  driver: null,
  checkVehicleSessionStatus: "idle",
  lastSession: null,
};

export const getDriverAsync = createAsyncThunk(
  "getDriver/call/",
  async (payload: GetDriverKnownPayloadType) => {
    const phoneToken = localStorage.getItem("phoneToken");
    if (!phoneToken) {
      throw Error();
    }
    const axios = axiosInstanceWithPhoneToken(phoneToken);
    const response = await axios.get<GetIsDriverKnownResponseType>(
      `driver_access/qr_code/${payload.uuid}/is_driver_already_known`,
    );
    return response.data;
  },
);

export const getLastVehicleSessionAsync = createAsyncThunk(
  "checkVehicleSession/call/",
  async (payload: { vehicleUuid: string }) => {
    const phoneToken = localStorage.getItem("phoneToken");
    if (!phoneToken) {
      throw Error();
    }
    const axios = axiosInstanceWithPhoneToken(phoneToken);
    const response = await axios.get<GetVehicleLastSessionResponse>(
      `driver_access/last_session/vehicle/${payload.vehicleUuid}`,
    );
    return response.data;
  },
);

export const driverKnown = createSlice({
  name: "driver_known",
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {
    resetGetVehicleSessionStatus: (state) => {
      state.checkVehicleSessionStatus = "idle";
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getDriverAsync.pending, (state) => {
        state.getIsDriverKnownStatus = "processing";
      })
      .addCase(getDriverAsync.fulfilled, (state, action) => {
        state.driver = action.payload.driverUuid;
        state.getIsDriverKnownStatus = "success";
      })
      .addCase(getDriverAsync.rejected, (state) => {
        state.getIsDriverKnownStatus = "failed";
      })
      .addCase(getLastVehicleSessionAsync.pending, (state) => {
        state.checkVehicleSessionStatus = "processing";
      })
      .addCase(getLastVehicleSessionAsync.fulfilled, (state, action) => {
        state.lastSession = action.payload;
        state.checkVehicleSessionStatus = "success";
      })
      .addCase(getLastVehicleSessionAsync.rejected, (state) => {
        state.checkVehicleSessionStatus = "failed";
      });
  },
});

// The function below is called a selector and allows us to select a value from
// the state. Selectors can also be defined inline where they're used instead of
// in the slice file. For example: `useSelector((state: RootState) => state.counter.value)`

export const selectGetIsDriverKnownStatus = (state: RootState) =>
  state.driverKnown.getIsDriverKnownStatus;

export const selectDriver = (state: RootState) => state.driverKnown.driver;

export const selectGetVehicleSessionStatus = (state: RootState) =>
  state.driverKnown.checkVehicleSessionStatus;

export const selectLastSession = (state: RootState) =>
  state.driverKnown.lastSession;

export default driverKnown.reducer;
